import { booleanLookup } from '../../../common/constants/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { IHyperV } from '../../../services/rvTools/hyperV/hyperV.model';
import { RootState } from '../../app.model';
import {
  searchHyperV,
  getHyperVById,
  saveHyperV,
  deleteHyperV,
} from './hyperV.action';
import { IHyperVState } from './hyperV.model';

export const initialState: IHyperVState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const hyperVSlice = createSlice({
  name: 'hyperV',
  initialState,
  reducers: {
    clearHyperV: () => {
      return initialState;
    },
    clearHyperVMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearHyperVGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchHyperV.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchHyperV.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<IHyperV>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchHyperV.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getHyperVById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getHyperVById.fulfilled.type]: (state, action: PayloadAction<IHyperV>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getHyperVById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveHyperV.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveHyperV.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveHyperV.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteHyperV.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteHyperV.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteHyperV.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const hyperVSelector = (state: RootState) => state.hyperV;

// Actions
export const {
  clearHyperV,
  clearHyperVMessages,
  clearHyperVGetById,
  setTableColumnSelection,
} = hyperVSlice.actions;

// The reducer
export default hyperVSlice.reducer;
